import { useState, useRef, useEffect } from "react";
import { X, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import client from "../lib/api";
import { toast } from "sonner";

export default function AIPanel({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm your ops copilot. Ask me to draft a dispatch note, a customer update, a payment reminder, a driver message, a lead follow-up, or anything else operational." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await client.post("/ai/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error("AI failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ai-panel">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-md bg-[#0F0F0F] border-l border-white/10 flex flex-col h-screen">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center">
              <Sparkle size={18} weight="fill" color="#007AFF" />
            </div>
            <div>
              <div className="font-display font-semibold">AI Copilot</div>
              <div className="text-[10px] text-neutral-500 tracking-widest uppercase">Powered by GPT-5.2</div>
            </div>
          </div>
          <button data-testid="close-ai-panel" onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-md px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#FF5A1F] text-black font-medium"
                  : "bg-[#141414] border border-white/10 text-neutral-200"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-md px-4 py-3 bg-[#141414] border border-white/10 text-sm text-neutral-300">
                <span className="stream-cursor">Thinking</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2 bg-[#141414] border border-white/10 rounded-md p-2 focus-within:border-[#FF5A1F]/50">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Ask anything about your ops..."
              className="flex-1 bg-transparent outline-none px-2 text-sm"
              disabled={loading}
            />
            <button
              data-testid="ai-send-btn"
              onClick={send}
              disabled={loading || !input.trim()}
              className="w-9 h-9 rounded-md bg-[#FF5A1F] hover:bg-[#FF7038] disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              <PaperPlaneTilt size={16} weight="fill" color="#0A0A0A" />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
